import { useState, useEffect } from "react";
import { useThemeContext } from "../../../../context/ThemeContext";
import { Info, CheckCircle, AlertCircle } from "lucide-react";

const Step4AssessmentMatrix = ({
  courseOutcomes,
  assessmentSetup,
  onBack,
  onNext,
  darkMode,
  initialData = null,
  onDataChange = () => {},
}) => {
  const [assessmentTools, setAssessmentTools] = useState(initialData || []);

  // Generate assessment tools based on setup
  const generateAssessmentTools = () => {
    if (initialData) return;

    const tools = [
      ...Array.from({ length: assessmentSetup.assignments }, (_, i) => ({
        tool_name: `A${i + 1}`,
        total_marks: 0,
        maximum_marks: 0,
        weightage: 0,
        sequence: i + 1,
        co_mapping: courseOutcomes.map((co) => ({
          co_id: co.co_id,
          mapped_marks: 0,
        })),
      })),
      ...Array.from({ length: assessmentSetup.classTests }, (_, i) => ({
        tool_name: `CT${i + 1}`,
        total_marks: 0,
        maximum_marks: 0,
        weightage: 0,
        sequence: assessmentSetup.assignments + i + 1,
        co_mapping: courseOutcomes.map((co) => ({
          co_id: co.co_id,
          mapped_marks: 0,
        })),
      })),
      {
        tool_name: "Attendance",
        total_marks: 0,
        maximum_marks: 0,
        weightage: 0,
        sequence: assessmentSetup.assignments + assessmentSetup.classTests + 1,
        co_mapping: courseOutcomes.map((co) => ({
          co_id: co.co_id,
          mapped_marks: 0,
        })),
      },
      {
        tool_name: "MTE",
        total_marks: 0,
        maximum_marks: 0,
        weightage: 0,
        sequence: assessmentSetup.assignments + assessmentSetup.classTests + 2,
        co_mapping: courseOutcomes.map((co) => ({
          co_id: co.co_id,
          mapped_marks: 0,
        })),
      },
      ...(assessmentSetup.includePRS
        ? [
            {
              tool_name: "PRS",
              total_marks: 0,
              maximum_marks: 0,
              weightage: 0,
              sequence:
                assessmentSetup.assignments + assessmentSetup.classTests + 3,
              co_mapping: courseOutcomes.map((co) => ({
                co_id: co.co_id,
                mapped_marks: 0,
              })),
            },
          ]
        : []),
      {
        tool_name: "ETE",
        total_marks: 0,
        maximum_marks: 0,
        weightage: 0,
        sequence:
          assessmentSetup.assignments +
          assessmentSetup.classTests +
          (assessmentSetup.includePRS ? 4 : 3),
        co_mapping: courseOutcomes.map((co) => ({
          co_id: co.co_id,
          mapped_marks: 0,
        })),
      },
    ];

    setAssessmentTools(tools);
    onDataChange(tools);
  };

  useEffect(() => {
    generateAssessmentTools();
  }, [assessmentSetup, courseOutcomes]);

  useEffect(() => {
    if (assessmentTools.length > 0) {
      onDataChange(assessmentTools);
    }
  }, [assessmentTools, onDataChange]);

  const handleValueChange = (toolIndex, field, value) => {
    if (value === "" || (!isNaN(value) && value >= 0)) {
      const updatedTools = [...assessmentTools];
      updatedTools[toolIndex][field] = value === "" ? "" : Number(value);
      setAssessmentTools(updatedTools);
    }
  };

  const handleCoMappingChange = (toolIndex, coIndex, value) => {
    if (value === "" || (!isNaN(value) && value >= 0)) {
      const updatedTools = [...assessmentTools];
      updatedTools[toolIndex].co_mapping[coIndex].mapped_marks =
        value === "" ? "" : Number(value);
      setAssessmentTools(updatedTools);
    }
  };

  const handleSubmit = () => {
    const processedTools = assessmentTools.map((tool) => ({
      ...tool,
      total_marks: tool.total_marks === "" ? 0 : Number(tool.total_marks),
      maximum_marks: tool.maximum_marks === "" ? 0 : Number(tool.maximum_marks),
      weightage: tool.weightage === "" ? 0 : Number(tool.weightage),
      co_mapping: tool.co_mapping.map((co) => ({
        ...co,
        mapped_marks: co.mapped_marks === "" ? 0 : Number(co.mapped_marks),
      })),
    }));

    const totalWeightage = processedTools.reduce(
      (sum, tool) => sum + tool.weightage,
      0
    );
    if (totalWeightage !== 100) {
      alert(`Total weightage must be 100 (current: ${totalWeightage})`);
      return;
    }

    for (const tool of processedTools) {
      const totalMapped = tool.co_mapping.reduce(
        (sum, co) => sum + co.mapped_marks,
        0
      );
      if (totalMapped !== tool.total_marks) {
        alert(
          `For ${tool.tool_name}, sum of CO mappings must equal total marks (${tool.total_marks})`
        );
        return;
      }
    }

    onNext(processedTools);
  };

  const totalWeightage = assessmentTools.reduce(
    (sum, tool) => sum + (tool.weightage === "" ? 0 : Number(tool.weightage)),
    0
  );

  return (
    <div
      className={`p-4 rounded-lg mb-4 transition-all duration-300 ${
        darkMode
          ? "bg-[#0D1117] border border-[#252525]"
          : "bg-white border border-[#E0E0E0] shadow-sm"
      }`}
    >
      <div className="mb-3">
        <h3
          className={`text-lg font-bold mb-1 ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          Assessment Matrix
        </h3>

        <p
          className={`text-sm flex items-start ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          <Info size={14} className="mr-1.5 mt-0.5 flex-shrink-0" />
          Map assessment tools to course outcomes and define their weightage.
        </p>
      </div>

      <div className="mb-3 flex justify-between items-center">
        <div
          className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          Total Weightage:{" "}
          <span
            className={
              totalWeightage === 100
                ? "text-green-600 font-medium"
                : "text-red-600 font-medium"
            }
          >
            {totalWeightage}%
          </span>
          {totalWeightage !== 100 && (
            <span className="text-sm ml-1">
              ({100 - totalWeightage}% remaining)
            </span>
          )}
        </div>

        {totalWeightage === 100 && (
          <div
            className={`text-sm flex items-center ${darkMode ? "text-green-400" : "text-green-600"}`}
          >
            <CheckCircle size={14} className="mr-1" />
            Complete
          </div>
        )}
      </div>

      <div className="overflow-x-auto text-sm">
        <table className="w-full">
          <thead>
            <tr
              className={`${
                darkMode
                  ? "bg-[#161B22] text-white"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              <th className="p-1.5 text-left">Tools</th>
              {assessmentTools.map((tool) => (
                <th key={tool.tool_name} className="p-1.5 text-center">
                  {tool.tool_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Total Marks Row */}
            <tr
              className={`border-b ${darkMode ? "border-[#252525]" : "border-gray-200"}`}
            >
              <td className="p-1.5 font-medium">Total Marks</td>
              {assessmentTools.map((tool, toolIndex) => (
                <td
                  key={`total-${tool.tool_name}`}
                  className="p-1.5 text-center"
                >
                  <input
                    type="number"
                    min="0"
                    value={tool.total_marks}
                    onChange={(e) =>
                      handleValueChange(
                        toolIndex,
                        "total_marks",
                        e.target.value
                      )
                    }
                    className={`w-12 p-1 rounded border ${
                      darkMode
                        ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                        : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
                    }`}
                  />
                </td>
              ))}
            </tr>

            {/* Maximum Marks Row */}
            <tr
              className={`border-b ${darkMode ? "border-[#252525]" : "border-gray-200"}`}
            >
              <td className="p-1.5 font-medium">Max Marks</td>
              {assessmentTools.map((tool, toolIndex) => (
                <td key={`max-${tool.tool_name}`} className="p-1.5 text-center">
                  <input
                    type="number"
                    min="0"
                    value={tool.maximum_marks}
                    onChange={(e) =>
                      handleValueChange(
                        toolIndex,
                        "maximum_marks",
                        e.target.value
                      )
                    }
                    className={`w-12 p-1 rounded border ${
                      darkMode
                        ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                        : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
                    }`}
                  />
                </td>
              ))}
            </tr>

            {/* Weightage Row */}
            <tr
              className={`border-b ${darkMode ? "border-[#252525]" : "border-gray-200"}`}
            >
              <td className="p-1.5 font-medium">Weightage (%)</td>
              {assessmentTools.map((tool, toolIndex) => (
                <td
                  key={`weight-${tool.tool_name}`}
                  className="p-1.5 text-center"
                >
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={tool.weightage}
                    onChange={(e) =>
                      handleValueChange(toolIndex, "weightage", e.target.value)
                    }
                    className={`w-12 p-1 rounded border ${
                      darkMode
                        ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                        : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
                    }`}
                  />
                </td>
              ))}
            </tr>

            {/* CO Mapping Rows */}
            {courseOutcomes.map((co, coIndex) => (
              <tr
                key={co.co_id}
                className={`border-b ${darkMode ? "border-[#252525]" : "border-gray-200"}`}
              >
                <td className="p-1.5 font-medium">CO{co.co_number}</td>
                {assessmentTools.map((tool, toolIndex) => (
                  <td
                    key={`${tool.tool_name}-${co.co_id}`}
                    className="p-1.5 text-center"
                  >
                    <input
                      type="number"
                      min="0"
                      max={
                        tool.total_marks === ""
                          ? undefined
                          : Number(tool.total_marks)
                      }
                      value={tool.co_mapping[coIndex].mapped_marks}
                      onChange={(e) =>
                        handleCoMappingChange(
                          toolIndex,
                          coIndex,
                          e.target.value
                        )
                      }
                      className={`w-12 p-1 rounded border ${
                        darkMode
                          ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                          : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
                      }`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-between items-center pt-3 gap-2">
        <button
          className={`px-3 py-1.5 rounded font-medium transition-colors w-full md:w-auto text-sm ${
            darkMode
              ? "bg-[#252525] text-white hover:bg-[#383838]"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          onClick={onBack}
        >
          Back
        </button>

        <button
          className={`px-3 py-1.5 rounded font-medium flex items-center justify-center transition-colors w-full md:w-auto text-sm ${"bg-[#A41E22] hover:bg-[#8A1A1E] text-white"}`}
          onClick={handleSubmit}
        >
          Save & Continue
        </button>
      </div>
    </div>
  );
};

export default Step4AssessmentMatrix;
